import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { 
  ArrowLeft, Package, Tag, MapPin, 
  Info, ShoppingBag, FileText, Sparkles, AlertCircle,
  CheckCircle, Circle, AlertTriangle, XCircle, Calendar, 
  Plus, Trash2, Euro, Clock
} from 'lucide-react';

const conditionOptions = [
  { value: 'excellent', label: 'Eccellente', icon: Sparkles, color: 'from-green-500 to-emerald-600', emoji: '✨' },
  { value: 'good', label: 'Buono', icon: CheckCircle, color: 'from-blue-500 to-blue-600', emoji: '👍' },
  { value: 'fair', label: 'Discreto', icon: Circle, color: 'from-yellow-500 to-yellow-600', emoji: '👌' },
  { value: 'poor', label: 'Scarso', icon: AlertTriangle, color: 'from-orange-500 to-orange-600', emoji: '⚠️' },
  { value: 'broken', label: 'Rotto', icon: XCircle, color: 'from-red-500 to-red-600', emoji: '❌' }
];

export default function NewAsset() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [condition, setCondition] = useState('good');
  const [serialNumber, setSerialNumber] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [notes, setNotes] = useState('');

  // Scheduled maintenances state
  interface ScheduledMaintenanceInput {
    id: string;
    description: string;
    intervalMonths: string;
    estimatedCost: string;
    referenceType: 'asset_creation' | 'purchase_date' | 'custom';
    customReferenceDate: string;
  }
  const [scheduledMaintenances, setScheduledMaintenances] = useState<ScheduledMaintenanceInput[]>([]);

  const addScheduledMaintenance = () => {
    setScheduledMaintenances([...scheduledMaintenances, {
      id: crypto.randomUUID(),
      description: '',
      intervalMonths: '12',
      estimatedCost: '',
      referenceType: 'asset_creation',
      customReferenceDate: ''
    }]);
  };

  const updateScheduledMaintenance = (id: string, field: keyof ScheduledMaintenanceInput, value: string) => {
    setScheduledMaintenances(scheduledMaintenances.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  const removeScheduledMaintenance = (id: string) => {
    setScheduledMaintenances(scheduledMaintenances.filter(m => m.id !== id));
  };

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: api.getCategories,
    refetchOnMount: 'always'
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
    refetchOnMount: 'always'
  });

  const createMutation = useMutation({
    mutationFn: api.createAsset,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/assets/${data.asset.id}`);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name,
      categoryId: categoryId || undefined,
      locationId: locationId || undefined,
      condition,
      serialNumber: serialNumber || undefined,
      manufacturer: manufacturer || undefined,
      model: model || undefined,
      purchaseDate: purchaseDate || undefined,
      purchasePrice: purchasePrice ? parseInt(purchasePrice) * 100 : undefined,
      notes: notes || undefined,
      scheduledMaintenances: scheduledMaintenances
        .filter(m => m.description.trim() && m.intervalMonths)
        .map(m => ({
          description: m.description.trim(),
          intervalMonths: parseInt(m.intervalMonths),
          estimatedCost: m.estimatedCost ? parseFloat(m.estimatedCost) : undefined,
          referenceType: m.referenceType,
          customReferenceDate: m.referenceType === 'custom' && m.customReferenceDate ? m.customReferenceDate : undefined
        }))
    });
  };

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/assets')}
          className="icon-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-5 h-5 text-primary-500" />
            <span className="text-sm font-medium text-primary-600">Creazione</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Nuovo Asset</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl">
              <Info className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Informazioni Base</h2>
          </div>

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Nome dell'asset..."
                required
                autoFocus
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label flex items-center gap-2">
                  <Tag className="w-4 h-4 text-gray-400" />
                  Categoria
                </label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="input"
                >
                  <option value="">Seleziona categoria...</option>
                  {categoriesData?.categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  Ubicazione
                </label>
                <select
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  className="input"
                >
                  <option value="">Seleziona ubicazione...</option>
                  {locationsData?.locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Condition Selection */}
            <div className="form-group">
              <label className="form-label">Condizione</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {conditionOptions.map((opt) => {
                  const Icon = opt.icon;
                  const isSelected = condition === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setCondition(opt.value)}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 scale-[1.02]'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">{opt.emoji}</span>
                      <span className={`text-xs font-medium ${isSelected ? 'text-primary-700' : 'text-gray-600'}`}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
              <Package className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Dettagli Prodotto</h2>
          </div>

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Numero Seriale</label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                className="input font-mono"
                placeholder="S/N..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Produttore</label>
                <input
                  type="text"
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                  className="input"
                  placeholder="Marca/Produttore..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Modello</label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="input"
                  placeholder="Modello..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Info */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Informazioni Acquisto</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Data Acquisto</label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Prezzo (€)</label>
              <input
                type="number"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                className="input"
                min="0"
                step="1"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Note</h2>
          </div>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input"
            rows={3}
            placeholder="Note aggiuntive..."
          />
        </div>

        {/* Scheduled Maintenances */}
        <div className="card">
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Manutenzioni Programmate</h2>
                <p className="text-xs text-gray-500">Definisci manutenzioni ricorrenti per questo asset</p>
              </div>
            </div>
            <button
              type="button"
              onClick={addScheduledMaintenance}
              className="btn btn-secondary btn-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Aggiungi
            </button>
          </div>

          {scheduledMaintenances.length === 0 ? (
            <div className="text-center py-6">
              <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nessuna manutenzione programmata</p>
              <p className="text-xs text-gray-400 mt-1">Clicca "Aggiungi" per inserire una manutenzione ricorrente</p>
            </div>
          ) : (
            <div className="space-y-4">
              {scheduledMaintenances.map((maintenance, index) => (
                <div key={maintenance.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Manutenzione {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeScheduledMaintenance(maintenance.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div className="form-group">
                      <label className="form-label text-xs">Descrizione *</label>
                      <input
                        type="text"
                        value={maintenance.description}
                        onChange={(e) => updateScheduledMaintenance(maintenance.id, 'description', e.target.value)}
                        className="input"
                        placeholder="es. Controllo generale, Sostituzione filtri..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="form-group">
                        <label className="form-label text-xs flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Intervallo (mesi) *
                        </label>
                        <select
                          value={maintenance.intervalMonths}
                          onChange={(e) => updateScheduledMaintenance(maintenance.id, 'intervalMonths', e.target.value)}
                          className="input"
                        >
                          <option value="1">Ogni mese</option>
                          <option value="2">Ogni 2 mesi</option>
                          <option value="3">Ogni 3 mesi</option>
                          <option value="6">Ogni 6 mesi</option>
                          <option value="12">Ogni anno</option>
                          <option value="18">Ogni 18 mesi</option>
                          <option value="24">Ogni 2 anni</option>
                          <option value="36">Ogni 3 anni</option>
                          <option value="60">Ogni 5 anni</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label text-xs flex items-center gap-1">
                          <Euro className="w-3 h-3" />
                          Costo stimato (€)
                        </label>
                        <input
                          type="number"
                          value={maintenance.estimatedCost}
                          onChange={(e) => updateScheduledMaintenance(maintenance.id, 'estimatedCost', e.target.value)}
                          className="input"
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label text-xs">Data di riferimento per il calcolo</label>
                      <select
                        value={maintenance.referenceType}
                        onChange={(e) => updateScheduledMaintenance(maintenance.id, 'referenceType', e.target.value as any)}
                        className="input"
                      >
                        <option value="asset_creation">Data inserimento asset</option>
                        <option value="purchase_date">Data acquisto</option>
                        <option value="custom">Data personalizzata</option>
                      </select>
                    </div>

                    {maintenance.referenceType === 'custom' && (
                      <div className="form-group">
                        <label className="form-label text-xs">Data personalizzata *</label>
                        <input
                          type="date"
                          value={maintenance.customReferenceDate}
                          onChange={(e) => updateScheduledMaintenance(maintenance.id, 'customReferenceDate', e.target.value)}
                          className="input"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {createMutation.error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 text-sm">{createMutation.error.message}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 sticky bottom-0 bg-gray-50/80 backdrop-blur-sm py-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:bg-transparent sm:backdrop-blur-none border-t border-gray-200 sm:border-0">
          <button
            type="button"
            onClick={() => navigate('/assets')}
            className="btn btn-secondary flex-1"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || !name.trim()}
            className="btn btn-primary flex-1"
          >
            {createMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Creazione...
              </>
            ) : (
              <>
                <Package className="w-4 h-4" />
                Crea Asset
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
